const SENSITIVE_KEYS = [
  'authorization',
  'password',
  'passwd',
  'pwd',
  'token',
  'tn',
  'apiKey',
  'api_key',
  'secret',
  'cookie',
];

const isSensitiveKey = (key) => {
  if (!key) return false;
  const lower = String(key).toLowerCase();
  return SENSITIVE_KEYS.some(item => lower.includes(item.toLowerCase()));
};

const sanitize = (value) => {
  if (value == null) return value;
  if (typeof value === 'string') {
    if (value.length > 120) return `${value.slice(0, 80)}...(${value.length})`;
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(item => sanitize(item));
  }
  if (typeof value === 'object') {
    const result = {};
    Object.keys(value).forEach(key => {
      result[key] = isSensitiveKey(key) ? '[REDACTED]' : sanitize(value[key]);
    });
    return result;
  }
  return value;
};

export const summarizeError = (error) => {
  if (!error) return {};
  return sanitize({
    name: error.name,
    message: error.message || String(error),
    code: error.code,
    status: error.response && error.response.status,
    responseCode: error.response && error.response.data && error.response.data.code,
    responseMsg: error.response && error.response.data && error.response.data.msg,
    url: error.config && error.config.url,
    method: error.config && error.config.method,
    baseURL: error.config && error.config.baseURL,
  });
};

const buildPayload = (code, message, detail = {}) => ({
  code,
  message,
  detail: sanitize(detail),
  href: typeof window !== 'undefined' ? window.location.href : '',
  ts: new Date().toISOString(),
});

const log = (level, code, message, detail) => {
  const payload = buildPayload(code, message, detail);
  const text = `[${code}] ${message}`;
  const logger = console[level] || console.log;
  logger(text, payload);
  return payload;
};

export const logInfo = (code, message, detail) => log('info', code, message, detail);
export const logWarn = (code, message, detail) => log('warn', code, message, detail);
export const logError = (code, message, detail) => log('error', code, message, detail);

export const installGlobalErrorHandlers = () => {
  if (typeof window === 'undefined' || window.__SJ_ERROR_LOGGER_INSTALLED__) {
    return;
  }

  window.__SJ_ERROR_LOGGER_INSTALLED__ = true;

  window.addEventListener('error', event => {
    logError('SJ-APP-001', 'global runtime error captured', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: summarizeError(event.error),
    });
  });

  window.addEventListener('unhandledrejection', event => {
    logError('SJ-APP-002', 'global unhandled promise rejection captured', {
      reason: summarizeError(event.reason),
      reasonMessage: event.reason && event.reason.message ? event.reason.message : String(event.reason),
    });
  });
};

const errorLogger = {
  info: logInfo,
  warn: logWarn,
  error: logError,
  summarizeError,
  installGlobalErrorHandlers,
};

export default errorLogger;
